import { useMemo, useState } from 'react'
import { ChevronDown, Loader2, Sparkles } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { useInvestigations, useMarkDraftUsed } from '@/queries/investigations'
import { isStale } from '@/types/investigation'
import { InvestigationPanel } from './InvestigationPanel'
import { summaryParagraphs } from './SummaryText'
import type { InvestigationWithTriage } from '@/queries/investigations'

function byCreatedAtDesc(a: InvestigationWithTriage, b: InvestigationWithTriage): number {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt)
}

// Same three bands ConfidenceMeter uses, as a tinted pill: the collapsed card
// has room for exactly one fact, and "how much should I trust this" is it.
function confidencePill(value: number): { label: string; className: string } {
  if (value < 40) return { label: 'advisory', className: 'bg-etn-warning-subtle text-etn-warning-text' }
  if (value <= 70) return { label: 'worth a look', className: 'bg-etn-info-subtle text-etn-info-text' }
  return { label: 'strong', className: 'bg-etn-success-subtle text-etn-success-text' }
}

/**
 * The AI investigation, re-housed in the rail as a collapsed card (approved
 * mockup, "Conversation + slim rail"). It used to render inline in the message
 * thread, where a long result pushed the actual conversation off screen and
 * read as a participant in it. Expanding shows the unchanged
 * InvestigationPanel — this is a container, not a second implementation of the
 * feature.
 */
export function InvestigationRailCard({ ticketId, onUseAsReply }: { ticketId: string; onUseAsReply: (text: string) => void }) {
  const { data } = useInvestigations(ticketId)
  const [open, setOpen] = useState(false)

  const runs = useMemo(() => [...(data?.data ?? [])].sort(byCreatedAtDesc), [data])
  const latest = runs[0] as InvestigationWithTriage | undefined
  if (!latest) return null

  const live = (latest.status === 'queued' || latest.status === 'running') && !isStale(latest)
  const confidence = latest.result?.confidence
  const pill = confidence == null ? null : confidencePill(confidence)

  return (
    <div className="mb-5" data-testid="investigation-rail-card">
      <h3 className="mb-2 text-etn-micro font-semibold uppercase tracking-[0.05em] text-etn-text-label">
        Support Agent
      </h3>
      <div className="overflow-hidden rounded-etn-card border border-etn-border-hairline bg-etn-surface shadow-etn-whisper">
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen(o => !o)}
          className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-etn-control font-semibold text-etn-text-cell"
        >
          {live ? <Loader2 className="h-3.5 w-3.5 animate-spin text-etn-text-soft" /> : <Sparkles className="h-3.5 w-3.5 text-etn-text-soft" />}
          {pill ? (
            <span className={`rounded-etn-pill px-2.5 py-0.5 text-etn-micro font-semibold ${pill.className}`}>
              {confidence}% — {pill.label}
            </span>
          ) : (
            <span className="text-etn-text-soft">{live ? 'Investigating…' : statusLabel(latest.status)}</span>
          )}
          <ChevronDown className={`ml-auto h-3.5 w-3.5 text-etn-text-soft transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="border-t border-etn-border-row">
            {latest.status === 'completed' && latest.result ? (
              <CompactSummary ticketId={ticketId} run={latest} onUseAsReply={onUseAsReply} />
            ) : (
              <InvestigationPanel ticketId={ticketId} onUseAsReply={onUseAsReply} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// The rail only summarises a finished run; the evidence, steps and proposals live on the full page.
function CompactSummary({ ticketId, run, onUseAsReply }: { ticketId: string; run: InvestigationWithTriage; onUseAsReply: (text: string) => void }) {
  const markDraftUsed = useMarkDraftUsed(ticketId, run._id)
  const result = run.result!
  const waiting = (run.proposedActions ?? []).filter(p => p.status === 'proposed').length
  // The rail has room for the gist only: the first paragraph, clamped. The full page shows the rest.
  const gist = summaryParagraphs(result.summary)[0] ?? result.summary
  return (
    <div className="space-y-2.5 px-3 py-3" data-testid="investigation-summary">
      <p className="text-etn-control font-semibold text-etn-text-cell">{result.title || gist}</p>
      {result.title && <p className="line-clamp-3 text-xs text-etn-text-soft">{gist}</p>}
      {waiting > 0 && (
        <p className="text-xs text-etn-text-label">
          {waiting} proposed {waiting === 1 ? 'change' : 'changes'} waiting for review
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button size="xs" asChild>
          <Link to="/admin/tickets/$id/investigation" params={{ id: ticketId }} search={{ run: run._id }}>
            Open investigation
          </Link>
        </Button>
        {result.draftReply && (
          <Button
            size="xs"
            variant="outline"
            onClick={() => {
              markDraftUsed.mutate()
              onUseAsReply(result.draftReply)
            }}
          >
            Use draft reply
          </Button>
        )}
      </div>
    </div>
  )
}

function statusLabel(status: InvestigationWithTriage['status']): string {
  if (status === 'failed') return 'Investigation failed'
  if (status === 'completed') return 'No result'
  return 'Investigation stalled'
}
